import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { EmptyState } from '@/components/EmptyState';
import { Topbar } from '@/components/Topbar';
import { useAsync } from '@/hooks/useAsync';

type LogScope = 'upcoming' | 'completed';

export function AccountLogsPage(): JSX.Element {
  const { id } = useParams();
  const accountId = Number(id);
  const [scope, setScope] = useState<LogScope>('upcoming');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { data: account } = useAsync(() => api.getAccount(accountId), [accountId]);
  const { data, loading, error } = useAsync(
    () => api.listAccountLogs(accountId, scope, page, pageSize),
    [accountId, scope, page],
  );

  const switchScope = (next: LogScope) => {
    setScope(next);
    setPage(1);
  };

  if (loading && !data) {
    return (
      <div className="space-y-3">
        <Topbar title="Логи" subtitle={account?.username ? `@${account.username}` : undefined} />
        <div className="skeleton h-12" />
        <div className="skeleton h-24" />
        <div className="skeleton h-24" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8">
      <Topbar
        title="Логи"
        subtitle={account?.name || account?.username || (account ? `Аккаунт #${account.id}` : undefined)}
      />

      <div className="flex gap-2 overflow-x-auto pb-1">
        <FilterButton active={scope === 'upcoming'} onClick={() => switchScope('upcoming')}>
          Предстоящие
        </FilterButton>
        <FilterButton active={scope === 'completed'} onClick={() => switchScope('completed')}>
          Выполненные
        </FilterButton>
      </div>

      {!data?.items.length ? (
        <EmptyState
          icon="search"
          title="Логи пусты"
          body={error || 'Под выбранный фильтр задач для этого аккаунта пока нет.'}
        />
      ) : (
        <>
          <ul className="space-y-3">
            {data.items.map((item) => (
              <li key={item.id} className="card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-[15px] font-semibold text-white">{item.targetTitle}</div>
                    <div className="mt-1 truncate text-[13px] text-[#7e87aa]">{item.campaignName}</div>
                  </div>
                  <span
                    className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${
                      item.type === 'join'
                        ? 'border-[#435bff] bg-[#121c49] text-[#cad4ff]'
                        : 'border-[#285b45] bg-[rgba(33,91,61,0.22)] text-[#59dc97]'
                    }`}
                  >
                    {item.type}
                  </span>
                </div>

                <div className="mt-4 grid gap-2 text-[13px] text-[#d9def5]">
                  <LogRow label="Статус" value={item.status} />
                  <LogRow label="Запланировано" value={formatDateTime(item.scheduledAt)} />
                  <LogRow label="Старт" value={item.startedAt ? formatDateTime(item.startedAt) : 'ещё нет'} />
                  <LogRow label="Финиш" value={item.finishedAt ? formatDateTime(item.finishedAt) : 'ещё нет'} />
                  <LogRow label="Ошибка" value={item.lastError || 'нет'} />
                </div>
              </li>
            ))}
          </ul>

          <div className="card flex items-center justify-between gap-3 p-4">
            <button
              disabled={!data || data.page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              className="btn-ghost min-w-[120px] disabled:opacity-50"
            >
              Назад
            </button>
            <div className="text-center text-[13px] text-[#7e87aa]">
              {data.page} / {data.totalPages}
              <div className="mt-1 text-[12px]">всего: {data.total}</div>
            </div>
            <button
              disabled={!data || data.page >= data.totalPages}
              onClick={() => setPage((current) => current + 1)}
              className="btn-primary min-w-[120px] disabled:opacity-50"
            >
              Дальше
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.12em] transition ${
        active
          ? 'border-[#435bff] bg-[#121c49] text-[#c9d3ff]'
          : 'border-white/10 bg-white/4 text-[#848cab]'
      }`}
    >
      {children}
    </button>
  );
}

function LogRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 rounded-[18px] border border-white/6 bg-white/4 px-4 py-3">
      <div className="text-[#8b94b7]">{label}</div>
      <div className="max-w-[60%] text-right text-white">{value}</div>
    </div>
  );
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}
